import React, { useState, useEffect, useCallback } from 'react';
import { expenseAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import {
  FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiFilter, FiRefreshCw, FiChevronDown, FiPaperclip
} from 'react-icons/fi';

const CATEGORIES = [
  'office_supplies','software','hardware','travel','food',
  'utilities','marketing','training','salary','rent','other'
];
const CAT_LABELS = {
  office_supplies:'Office Supplies', software:'Software', hardware:'Hardware',
  travel:'Travel', food:'Food & Dining', utilities:'Utilities',
  marketing:'Marketing', training:'Training', salary:'Salary', rent:'Rent', other:'Other'
};

const STATUS_CFG = {
  pending:  { bg:'#fef3c7', text:'#92400e', dot:'#f59e0b', label:'Pending' },
  approved: { bg:'#dcfce7', text:'#14532d', dot:'#22c55e', label:'Approved' },
  rejected: { bg:'#fee2e2', text:'#991b1b', dot:'#ef4444', label:'Rejected' },
};

const INP = {
  width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10,
  fontSize:14, outline:'none', boxSizing:'border-box', background:'#fafbfc',
  fontFamily:"'DM Sans',sans-serif", transition:'border .2s'
};
const SEL = { ...INP, cursor:'pointer' };
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const today = () => new Date().toISOString().slice(0,10);

const EMPTY_FORM = { title:'', description:'', amount:'', currency:'INR', category:'other', date:today(), projectId:'', notes:'', isBillable:false };

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();

  const [expenses, setExpenses] = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);
  const [approving,setApproving]= useState(null); // expenseId being approved
  const [reject,   setReject]   = useState({ open:false, id:null, reason:'' });
  const [filters,  setFilters]  = useState({ status:'', category:'', startDate:'', endDate:'' });
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [expR, statR] = await Promise.all([
        expenseAPI.getAll(filters),
        expenseAPI.getStats(),
      ]);
      setExpenses(expR.data.expenses || []);
      setStats(statR.data);
    } catch { toast.error('Failed to load expenses'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY_FORM, date: today() }); setModal(true); };
  const openEdit = (e) => {
    setEditing(e._id || e.id);
    setForm({ title:e.title, description:e.description||'', amount:e.amount, currency:e.currency||'INR',
      category:e.category||'other', date:e.date?.slice(0,10)||today(), projectId:e.projectId||'', notes:e.notes||'', isBillable:!!e.isBillable });
    setModal(true);
  };

  const handleSave = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      if (editing) { await expenseAPI.update(editing, form); toast.success('Expense updated'); }
      else         { await expenseAPI.create(form);          toast.success('Expense submitted!'); }
      setModal(false); load();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Delete this expense?', { title: 'Delete Expense?', variant: 'danger' });
    if (!ok) return;
    try { await expenseAPI.delete(id); toast.success('Deleted'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleApprove = async (id) => {
    setApproving(id);
    try { await expenseAPI.approve(id, { action:'approved' }); toast.success('Expense approved'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setApproving(null);
  };

  const handleReject = async () => {
    try { await expenseAPI.approve(reject.id, { action:'rejected', rejectionReason: reject.reason }); toast.success('Expense rejected'); setReject({ open:false, id:null, reason:'' }); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const StatusBadge = ({ status }) => {
    const c = STATUS_CFG[status] || STATUS_CFG.pending;
    return (
      <span style={{ background:c.bg, color:c.text, padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, display:'inline-flex', alignItems:'center', gap:5 }}>
        <span style={{ width:6, height:6, borderRadius:'50%', background:c.dot, display:'inline-block' }} />
        {c.label}
      </span>
    );
  };

  const totalStat = stats?.total || '0.00';
  const pendCount = stats?.pending?.count || 0;
  const appAmt    = stats?.approved?.amount || '0.00';
  const rejCount  = stats?.rejected?.count || 0;

  return (
    <div style={{ padding:'28px 32px', background:'#f8fafc', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:700, color:'#1e293b' }}>Expenses</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:14 }}>Track and manage all expense claims</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => setShowFilters(v => !v)} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#475569' }}>
            <FiFilter size={15}/> Filters <FiChevronDown size={13}/>
          </button>
          <button onClick={load} style={{ padding:'9px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', color:'#475569' }}>
            <FiRefreshCw size={15}/>
          </button>
          <button onClick={openAdd} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 }}>
            <FiPlus size={16}/> Add Expense
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          {[
            { label:'Total Spent',       value: fmt(totalStat), icon:'💰', color:'var(--pm-primary)', bg:'#eef2ff' },
            { label:'Pending Approval',  value: pendCount + ' expense' + (pendCount !== 1 ? 's' : ''), icon:'⏳', color:'#f59e0b', bg:'#fef3c7' },
            { label:'Approved Amount',   value: fmt(appAmt), icon:'✅', color:'#22c55e', bg:'#dcfce7' },
            { label:'Rejected',          value: rejCount + ' expense' + (rejCount !== 1 ? 's' : ''), icon:'❌', color:'#ef4444', bg:'#fee2e2' },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius:14, padding:'18px 20px', boxShadow:'0 1px 4px #0000000d', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div style={{ background:'#fff', borderRadius:14, padding:'16px 20px', marginBottom:20, boxShadow:'0 1px 4px #0000000d', display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ flex:1, minWidth:140 }}>
            <label style={{ fontSize:12, color:'#64748b', fontWeight:600, display:'block', marginBottom:6 }}>STATUS</label>
            <select value={filters.status} onChange={e => setFilters(f => ({...f, status:e.target.value}))} style={SEL}>
              <option value="">All Statuses</option>
              {['pending','approved','rejected'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:160 }}>
            <label style={{ fontSize:12, color:'#64748b', fontWeight:600, display:'block', marginBottom:6 }}>CATEGORY</label>
            <select value={filters.category} onChange={e => setFilters(f => ({...f, category:e.target.value}))} style={SEL}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:140 }}>
            <label style={{ fontSize:12, color:'#64748b', fontWeight:600, display:'block', marginBottom:6 }}>FROM</label>
            <input type="date" value={filters.startDate} onChange={e => setFilters(f => ({...f, startDate:e.target.value}))} style={INP}/>
          </div>
          <div style={{ flex:1, minWidth:140 }}>
            <label style={{ fontSize:12, color:'#64748b', fontWeight:600, display:'block', marginBottom:6 }}>TO</label>
            <input type="date" value={filters.endDate} onChange={e => setFilters(f => ({...f, endDate:e.target.value}))} style={INP}/>
          </div>
          <button onClick={() => setFilters({ status:'', category:'', startDate:'', endDate:'' })} style={{ padding:'10px 16px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13, color:'#64748b', whiteSpace:'nowrap' }}>
            Clear Filters
          </button>
        </div>
      )}

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #f1f5f9' }}>
              {['Date','Title','Category','Submitted By','Amount','Status','Actions'].map(h => (
                <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.5px', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding:48, textAlign:'center', color:'#94a3b8' }}>Loading…</td></tr>
            ) : expenses.length === 0 ? (
              <tr><td colSpan={7} style={{ padding:48, textAlign:'center', color:'#94a3b8' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🧾</div>
                <div style={{ fontWeight:600, marginBottom:4 }}>No expenses found</div>
                <div style={{ fontSize:13 }}>Submit your first expense claim</div>
              </td></tr>
            ) : expenses.map(e => (
              <tr key={e._id || e.id} style={{ borderBottom:'1px solid #f1f5f9', transition:'background .15s' }}
                onMouseEnter={ev => ev.currentTarget.style.background='#f8fafc'}
                onMouseLeave={ev => ev.currentTarget.style.background='#fff'}>
                <td style={{ padding:'13px 16px', fontSize:14, color:'#475569', whiteSpace:'nowrap' }}>
                  {e.date ? new Date(e.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                </td>
                <td style={{ padding:'13px 16px' }}>
                  <div style={{ fontWeight:600, color:'#1e293b', fontSize:14 }}>{e.title}</div>
                  {e.project && <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>📁 {e.project.name}</div>}
                  {e.notes && <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>📝 {e.notes.slice(0,40)}{e.notes.length>40?'…':''}</div>}
                </td>
                <td style={{ padding:'13px 16px' }}>
                  <span style={{ background:'#f1f5f9', color:'#475569', padding:'3px 10px', borderRadius:8, fontSize:12, fontWeight:600 }}>
                    {CAT_LABELS[e.category] || e.category}
                  </span>
                </td>
                <td style={{ padding:'13px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:30, height:30, borderRadius:'50%', background:'#e0e7ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'var(--pm-primary)' }}>
                      {(e.submittedBy?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>{e.submittedBy?.name || 'Unknown'}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{e.submittedBy?.email || ''}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding:'13px 16px', fontWeight:700, color:'#1e293b', fontSize:15, whiteSpace:'nowrap' }}>
                  {fmt(e.amount)}
                  {e.isBillable && <span style={{ fontSize:10, color:'var(--pm-primary)', marginLeft:5, fontWeight:600 }}>BILLABLE</span>}
                </td>
                <td style={{ padding:'13px 16px' }}>
                  <StatusBadge status={e.status}/>
                  {e.status === 'rejected' && e.rejectionReason && (
                    <div style={{ fontSize:11, color:'#ef4444', marginTop:4 }} title={e.rejectionReason}>
                      {e.rejectionReason.slice(0,30)}{e.rejectionReason.length>30?'…':''}
                    </div>
                  )}
                </td>
                <td style={{ padding:'13px 16px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    {isAdmin && e.status === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(e._id || e.id)} disabled={approving === (e._id||e.id)}
                          style={{ padding:'5px 10px', borderRadius:8, border:'none', background:'#dcfce7', color:'#14532d', cursor:'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}
                          title="Approve">
                          <FiCheck size={13}/> OK
                        </button>
                        <button onClick={() => setReject({ open:true, id:e._id||e.id, reason:'' })}
                          style={{ padding:'5px 10px', borderRadius:8, border:'none', background:'#fee2e2', color:'#991b1b', cursor:'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}
                          title="Reject">
                          <FiX size={13}/> Reject
                        </button>
                      </>
                    )}
                    {(isAdmin || (e.userId === user?.id && e.status === 'pending')) && (
                      <button onClick={() => openEdit(e)} style={{ padding:'5px 9px', borderRadius:8, border:'none', background:'#e0e7ff', color:'#4338ca', cursor:'pointer' }} title="Edit">
                        <FiEdit2 size={13}/>
                      </button>
                    )}
                    {(isAdmin || e.userId === user?.id) && (
                      <button onClick={() => handleDelete(e._id || e.id)} style={{ padding:'5px 9px', borderRadius:8, border:'none', background:'#fee2e2', color:'#dc2626', cursor:'pointer' }} title="Delete">
                        <FiTrash2 size={13}/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'#0006', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:'28px 32px', width:540, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 25px 60px #0000003a' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#1e293b' }}>{editing ? 'Edit Expense' : 'Add Expense'}</h2>
              <button onClick={() => setModal(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Title *</label>
                <input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required style={INP} placeholder="e.g. Team lunch, AWS subscription…"/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Amount (₹) *</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))} required style={INP} placeholder="0.00"/>
                </div>
                <div>
                  <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm(f=>({...f,date:e.target.value}))} required style={INP}/>
                </div>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Category</label>
                <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))} style={SEL}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} style={{ ...INP, minHeight:70, resize:'vertical' }} placeholder="Optional details…"/>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Notes</label>
                <input value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} style={INP} placeholder="Internal notes…"/>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:6 }}>Receipt / Attachment</label>
                <input type="file" accept="image/*,application/pdf" style={{ fontSize:13, color:'#475569', cursor:'pointer' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('File too large (max 5MB)'); return; }
                    const reader = new FileReader();
                    reader.onload = ev => setForm(f => ({ ...f, receipt: ev.target.result }));
                    reader.readAsDataURL(file);
                  }}/>
                {form.receipt && <div style={{ marginTop:6, fontSize:12, color:'#22c55e', fontWeight:600 }}>✓ Receipt attached</div>}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <input type="checkbox" id="billable" checked={form.isBillable} onChange={e => setForm(f=>({...f,isBillable:e.target.checked}))} style={{ width:16, height:16, cursor:'pointer' }}/>
                <label htmlFor="billable" style={{ fontSize:13, color:'#475569', cursor:'pointer', fontWeight:600 }}>Mark as billable to client</label>
              </div>
              <div style={{ display:'flex', gap:12, marginTop:8 }}>
                <button type="button" onClick={() => setModal(false)} style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#64748b', fontWeight:600 }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>
                  {saving ? 'Saving…' : (editing ? 'Update' : 'Submit Expense')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {reject.open && (
        <div style={{ position:'fixed', inset:0, background:'#0006', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:'28px', width:420, boxShadow:'0 20px 60px #0000003a' }}>
            <h3 style={{ margin:'0 0 16px', color:'#1e293b' }}>Reject Expense</h3>
            <p style={{ margin:'0 0 14px', color:'#64748b', fontSize:14 }}>Please provide a reason for rejection:</p>
            <textarea value={reject.reason} onChange={e => setReject(r=>({...r,reason:e.target.value}))} style={{ ...INP, minHeight:80, resize:'vertical', marginBottom:16 }} placeholder="Rejection reason…"/>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setReject({ open:false, id:null, reason:'' })} style={{ flex:1, padding:'10px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#64748b', fontWeight:600 }}>Cancel</button>
              <button onClick={handleReject} style={{ flex:1, padding:'10px', borderRadius:10, border:'none', background:'#ef4444', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
